import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Tag } from "@dashboard/shared/components/Tag/Tag";
import { Box, Theme, Typography } from "@mui/material";
import { PrimaryOutlinedButton } from "@shared/CustomButton";
import { UncontrolledTextField } from "@shared/form/TextField";

const useStyles = makeStyles()((theme: Theme) => ({
    content: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
    tagsArea: {
        display: "flex",
        flexWrap: "wrap",
        alignContent: "flex-start",
        minHeight: 88,
        width: "100%",
        border: "1px solid" + theme.palette.emteria.border.primary,
        borderRadius: 4,
        boxSizing: "border-box",
        padding: theme.spacing(2),
        gap: theme.spacing(1),
        backgroundColor: theme.palette.emteria.background.default,
    },
    tagsAreaEmpty: {
        alignContent: "center",
        justifyContent: "center",
    },
    placeholder: {
        fontSize: 14,
        color: theme.palette.emteria.text.secondary,
        textAlign: "center",
    },
    tagInput: {
        display: "flex",
        gap: theme.spacing(1),
    },
}));

type Props = {
    tags: API.Tag[];
    onChange: (tags: API.Tag[]) => void;
};
type ProcessedProps = {
    tags: Record<string, API.Tag>;
    onChange: (tags: API.Tag[]) => void;
};

const withProcessedProps = (Component: React.FC<ProcessedProps>) => {
    const WrappedComponent: React.FC<Props> = (props: Props) => {
        return <Component tags={_.keyBy(props.tags, "key")} onChange={props.onChange} />;
    };

    WrappedComponent.displayName = `withProcessedProps(${Component.displayName || Component.name || "TagManagementSection"})`;

    return WrappedComponent;
};
export const TagManagementSection = withProcessedProps((props: ProcessedProps) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["common"]);
    const [tagInput, setTagInput] = React.useState("");
    const [error, setError] = React.useState<string | null>(null);

    const tagsArr = Object.values(props.tags);

    const onRemove = (tag: API.Tag) => {
        const tagsCopy = { ...props.tags };
        delete tagsCopy[tag.key];
        props.onChange(Object.values(tagsCopy));
    };

    const onAdd = () => {
        const [key, value] = tagInput.trim().split("::");
        const type = value ? "scoped" : "simple";

        if (!key) {
            setError(t("common:section.tagManagement.input.tag.validation.cannotBeEmpty"));
            return;
        }

        if (/^\d+$/.test(key)) {
            setError(t("common:section.tagManagement.input.tag.validation.onlyNumbers"));
            return;
        }

        const tag: API.Tag = { key, value, type };

        const tagsCopy = { ...props.tags, [key]: tag };
        props.onChange(Object.values(tagsCopy));

        setTagInput("");
        setError(null);
    };

    return (
        <Box className={classes.content}>
            <Box className={classes.tagInput}>
                <UncontrolledTextField
                    label={t("common:section.tagManagement.input.tag.caption")}
                    placeholder={t("common:section.tagManagement.input.tag.placeholder")}
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={tagInput}
                    onChange={(e) => {
                        setTagInput(e.target.value);
                        if (error) {
                            setError(null);
                        }
                    }}
                    errorMessage={error ?? undefined}
                />
                <PrimaryOutlinedButton type="button" onClick={onAdd}>
                    {t("common:button.add")}
                </PrimaryOutlinedButton>
            </Box>
            <Box className={cx(classes.tagsArea, !tagsArr.length && classes.tagsAreaEmpty)}>
                {tagsArr.length ? (
                    tagsArr.map((item) => <Tag key={item.key} item={item} onClick={onRemove} />)
                ) : (
                    <Typography className={classes.placeholder}>{t("common:section.tagManagement.noTagsHint")}</Typography>
                )}
            </Box>
        </Box>
    );
});
